import { useId, useState } from "react";
import type { MaterialsRow } from "@workspace/api-zod/materials-list";
import { materialMeasureUnit, materialPurchaseUnit, purchaseUnitLabel } from "./material-purchase-units";

const format = (value: number) => value.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 4 });

export function MaterialOrderQuantity({ row, onChange }: {
  row: MaterialsRow; onChange?: (row: MaterialsRow, quantity: number | null) => void;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  const [error, setError] = useState("");
  const errorId = useId();
  const quantity = row.orderQuantity ?? row.quantity;
  const purchase = materialPurchaseUnit(row);
  const sourceQuantity = row.roundPacks && row.packSize ? row.roundedQuantity : quantity;
  const displayQuantity = purchase.whole ? Math.ceil(sourceQuantity / purchase.size - 1e-9) : sourceQuantity;
  const converted = purchase.unit !== materialMeasureUnit(row.unit) || displayQuantity !== sourceQuantity;
  const displayFormat = (value: number) => purchase.whole ? value.toLocaleString("en-GB") : format(value);
  const commit = () => {
    const value = Number(draft);
    if (!draft?.trim() || !Number.isFinite(value) || value < 0 || value > 1_000_000
      || (purchase.whole ? !Number.isInteger(value) : Math.abs(value * 10000 - Math.round(value * 10000)) > .00001)
      || value * purchase.size > 1_000_000) {
      setError(purchase.whole ? "Enter a whole number from 0–1,000,000 within the purchase quantity limit."
        : "Enter 0–1,000,000 with up to four decimal places.");
      return;
    }
    // Persist in the recipe's original unit; never reinterpret stored measurements.
    onChange?.(row, Math.round(value * purchase.size * 10_000) / 10_000);
    setDraft(null); setError("");
  };
  return <div>
    {draft === null ? <span className="tabular-nums" data-testid={`text-quantity-${row.key}`}>
      {onChange ? <button type="button" className="underline decoration-dotted underline-offset-4 text-primary py-1"
        aria-label={`Edit order quantity for ${row.name}`}
        title={`Purchase quantity in ${purchaseUnitLabel(purchase.unit, 2)} only; does not change work measurements or quote prices.`}
        onClick={() => { setDraft(String(displayQuantity)); setError(""); }}>
        {displayFormat(displayQuantity)}
      </button> : displayFormat(displayQuantity)}
      <small className="ml-1 text-muted-foreground">{purchaseUnitLabel(purchase.unit, displayQuantity)}</small>
    </span> : <input autoFocus type="text" inputMode="decimal"
      aria-label={`Order quantity for ${row.name}`} aria-invalid={!!error}
      aria-describedby={error ? errorId : undefined}
      title={`Quantity in ${purchaseUnitLabel(purchase.unit, 2)}`}
      className="h-8 w-24 max-w-full rounded border bg-background px-2 text-sm text-right"
      value={draft} onChange={event => setDraft(event.target.value)} onBlur={commit}
      onKeyDown={event => {
        if (event.key === "Enter") { event.preventDefault(); commit(); }
        if (event.key === "Escape") { event.preventDefault(); setDraft(null); setError(""); }
      }} />}
    {error && <p id={errorId} role="alert" className="text-xs text-destructive">{error}</p>}
    {converted && <div className="text-[10px] text-muted-foreground" data-testid={`text-measured-quantity-${row.key}`}>
      {format(quantity)} {row.unit} required
    </div>}
    {purchase.missing && <div className="text-[10px] text-amber-700 dark:text-amber-400">Purchase size needed</div>}
    {row.orderQuantity !== undefined && <div className="text-xs text-muted-foreground">
      Calculated: {format(row.quantity)} {row.unit}
      {onChange && <button type="button" className="ml-1 underline underline-offset-2"
        aria-label={`Reset order quantity for ${row.name}`} onClick={() => {
          setDraft(null); setError(""); onChange(row, null);
        }}>Reset</button>}
    </div>}
  </div>;
}
